import { RootState } from '@/app/store';
import { OrderSide, OrderStatus, OrderType } from '@/shared/constants/commonTypes';
import { isData } from '@/shared/helpers/websocket';
import { socket, waitForOpenConnection } from '@/shared/websocket/socket';
import { Order, SocketChannelName, camelizeNestedKeys } from '@cex/shared-modules';
import { EntityState, createEntityAdapter, createSelector } from '@reduxjs/toolkit';
import { mainEmptySplitApi } from '@/features/main/mainApi';

type OrderBase = {
  market: string;
};

// TODO: update types in shared-modules
type OrderBuyMarketPrice = OrderBase & {
  quoteQuantity: string;
  side: OrderSide.BUY;
  stopPrice?: string;
  type: OrderType.Market;
};

type OrderSellMarketPrice = OrderBase & {
  quantity: string;
  side: OrderSide.SELL;
  stopPrice?: string;
  type: OrderType.Market;
};

type OrderBuyLimit = OrderBase & {
  type: OrderType.Limit;
  quantity: string;
  price: string;
  stopPrice?: string;
  side: OrderSide.BUY;
};

type OrderSellLimit = OrderBase & {
  type: OrderType.Limit;
  quantity: string;
  price: string;
  stopPrice?: string;
  side: OrderSide.SELL;
};

type OrderParams = OrderBuyMarketPrice | OrderSellMarketPrice | OrderBuyLimit | OrderSellLimit;

type OrderDataType = {
  currentMarket: string;
  order: OrderBase & OrderParams;
};

const ordersAdapter = createEntityAdapter<Order>({
  selectId: (order) => order.id
});

const initialState = ordersAdapter.getInitialState();

export const ordersApi = mainEmptySplitApi.injectEndpoints({
  endpoints: (builder) => ({
    getOrders: builder.query<EntityState<Order>, { market: string; orderStatus: OrderStatus }>({
      query: ({ market, orderStatus }) => {
        return `/${market}/order?limit=&order_status=${orderStatus}`;
      },
      transformResponse(response: Order[]) {
        return ordersAdapter.setAll(initialState, camelizeNestedKeys(response));
      },
      async onCacheEntryAdded(arg, { updateCachedData, cacheDataLoaded, cacheEntryRemoved }) {
        // Websocket implementation: https://redux-toolkit.js.org/rtk-query/usage/streaming-updates#websocket-chat-api
        const { market } = arg;

        try {
          await cacheDataLoaded;
          await waitForOpenConnection();

          socket.subscribe(SocketChannelName.ORDER, market);
          const listener = (event: MessageEvent) => {
            const data = JSON.parse(event.data);

            if (data.subchan_name !== market || data.chan_name !== SocketChannelName.ORDER || !isData(data)) {
              return;
            }

            updateCachedData((draft) => {
              ordersAdapter.upsertOne(draft, data.data[0]);
            });
          };
          socket.addEventListener('message', listener);
        } catch (e) {
          console.error('ORDER CACHE', e);
        }
        // cacheEntryRemoved will resolve when the cache subscription is no longer active
        await cacheEntryRemoved;
        // perform cleanup steps once the `cacheEntryRemoved` promise resolves
        socket.close();
      }
    }),
    createOrder: builder.mutation({
      query: ({ order, currentMarket }: OrderDataType) => {
        const { market, side, type } = order;
        const bodyObj = {
          market,
          side,
          type,
          ...(order.type === OrderType.Market && order.side === OrderSide.BUY
            ? {
                quote_quantity: order.quoteQuantity
              }
            : {
                quantity: order.quantity
              }),
          ...(order.type === OrderType.Limit
            ? {
                price: order.price
              }
            : {}),
          ...(order.stopPrice !== undefined ? { stop_price: order.stopPrice } : {})
        };

        return {
          url: `${currentMarket}/order`,
          method: 'POST',
          body: bodyObj
        };
      },
      invalidatesTags: ['Orders']
    }),
    cancelOrder: builder.mutation({
      query: ({ market, id }: Order) => {
        return {
          url: `${market}/order/cancel`,
          method: 'POST',
          body: [id]
        };
      }
    })
  })
});

// @ts-ignore
const selectOrdersResult = ordersApi.endpoints.getOrders.select();

export const selectOrdersData = createSelector(selectOrdersResult, (apiSlice) => apiSlice.data);

export const { selectAll: selectAllOrders, selectById: selectOrderById } = ordersAdapter.getSelectors(
  (state: RootState) => selectOrdersData(state) ?? initialState
);

export const { useCreateOrderMutation, useGetOrdersQuery, useCancelOrderMutation } = ordersApi;
